import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { computed, reaction } from "mobx"
import { observer, useLocalObservable } from "mobx-react"
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Layout,
  Radio,
  RadioChangeEvent,
  Row,
  Select,
  Switch,
  Typography,
} from "antd"
import { EditOutlined, SaveOutlined } from "@ant-design/icons"

import { notify } from "@src/app/components/notify"
import { DefaultPageLayout } from "@src/app/layouts/DefaultPageLayout"
import { paths } from "@src/app/paths"
import { RegistrationPasswordField } from "@src/pages/registration/ui/authForm/ui/RegistrationPasswordField"
import { SubMerchant } from "@src/pages/subMerchSlug/clasess/SubMerch"
import { SubMerchAccessRights } from "@src/pages/subMerchSlug/clasess/SubMerchAccessRights"
import {
  SubMerchChangePasswordForm,
  SubMerchChangePasswordValues,
} from "@src/pages/subMerchSlug/ui/SubMerchChangePasswordForm"
import { SubmitButton } from "@src/shared/componemts/SubmitButton"
import { useCatchFormErrors } from "@src/shared/form/base_error"
import css from "./index.module.css"

type SubMerchFormValues = {
  access_control_groups?: Record<string, boolean>
  confirm_password: string
  email: string
  password: string
  status?: string
}

const normalizeFormValues = (values: Partial<SubMerchFormValues> = {}) => ({
  access_control_groups: Object.entries(values.access_control_groups ?? {})
    .filter(([, checked]) => checked)
    .map(([key]) => key)
    .sort(),
  email: values.email ?? "",
  status: values.status ?? "",
})

export const SubMerchSlug = observer(() => {
  const { Item } = Form
  const [form] = Form.useForm<SubMerchFormValues>()
  const [initialFormValues, setInitialFormValues] = useState<
    Partial<SubMerchFormValues>
  >({
    access_control_groups: { view: true },
  })

  const currentFormValues = Form.useWatch([], form)

  const { id } = useParams<{
    id: string
  }>()

  const ID = useMemo(() => (id === "new" ? "" : id), [id])

  const access = useLocalObservable(() => new SubMerchAccessRights())

  const nav = useNavigate()

  const account = useLocalObservable(() => new SubMerchant(ID))
  const [isPasswordLoading, setIsPasswordLoading] = useState(false)

  useEffect(() => {
    reaction(
      () => account.data,
      (v) => {
        if (v) {
          const f = {
            access_control_groups: v.access_control_groups.reduce(
              (acc, value) => {
                if (!acc[value]) {
                  acc[value] = true
                }

                return acc
              },
              {} as Record<string, boolean>,
            ),
            email: v.email,
            status: v.status,
          }

          form.setFieldsValue(f)
          setInitialFormValues(f)
        }
      },
    )
  }, [account.data])

  const { submit } = useCatchFormErrors(form, async (values) => {
    const access_control_groups = Object.entries(
      values.access_control_groups || {},
    )
      .filter(([_, checked]) => checked)
      .map(([key]) => key)

    const createReq = {
      ...values,
      access_control_groups,
    }

    const updateReq = {
      access_control_groups,
      status: form.getFieldValue("status"),
    }

    return ID
      ? account.update(ID, updateReq).then((response) => {
          if (response) {
            notify.success("Sub merchant has been updated")
            setInitialFormValues(form.getFieldsValue())
          }
        })
      : account.create(createReq).then((response) => {
          if (response) {
            notify.success("Sub merchant has been created")

            nav(`${paths.settings}?tab=tp`)
          }
        })
  })

  const changePassword = async (values: SubMerchChangePasswordValues) => {
    setIsPasswordLoading(true)

    try {
      const response = await account.changePassword(values)

      if (response) {
        notify.success("Password has been changed")
      }

      return response
    } finally {
      setIsPasswordLoading(false)
    }
  }

  const isFormChanged =
    JSON.stringify(
      normalizeFormValues(currentFormValues ?? initialFormValues),
    ) !== JSON.stringify(normalizeFormValues(initialFormValues))

  const button = computed(() => {
    if (account.isReadonly) {
      return (
        <Button
          icon={<EditOutlined />}
          loading={access.isLoading}
          onClick={() => account.setIsReadonly(false)}
          type="primary"
        >
          Edit
        </Button>
      )
    }

    return (
      <SubmitButton
        buttonProps={{
          disabled: account.isLoading || !isFormChanged,
          icon: <SaveOutlined />,
          loading: access.isLoading,
          onClick: () => submit(form.getFieldsValue()),

          type: "primary",
        }}
        form={form}
      >
        Save
      </SubmitButton>
    )
  })

  return (
    <DefaultPageLayout
      button={button.get()}
      isLoading={account.isLoading || access.isLoading}
      title="Add a member"
      url={`${paths.settings}?tab=tp`}
    >
      <>
        <Form
          disabled={account.isLoading || account.isReadonly}
          form={form}
          id="GENERATED_ID_BANKDETAILSSLUG_FORM_VOID_1"
          layout="vertical"
          name="subMerch"
          onFinish={submit}
          size="large"
          style={{ maxWidth: "100%" }}
        >
          <Flex gap={16} vertical>
            <Typography.Text style={{ fontSize: 16, fontWeight: 400 }}>
              <Typography.Text style={{ color: "#FF4D4F", fontSize: 16 }}>
                Important!
              </Typography.Text>{" "}
              You are responsible for securely sharing the login and password
              with the submerchant.
            </Typography.Text>
            <Row gutter={32}>
              <Col span={12}>
                <Flex gap={16} vertical>
                  <Typography.Title style={{ fontSize: 16, opacity: 0.65 }}>
                    MEMBER INFO
                  </Typography.Title>

                  <Item
                    label="Email"
                    name="email"
                    rules={[
                      { message: "Please input email", required: true },
                      {
                        message: "Must be a valid email address",
                        type: "email",
                      },
                    ]}
                  >
                    <Input placeholder="Enter email" />
                  </Item>

                  {account.isNew ? (
                    <Flex gap={16} vertical>
                      <RegistrationPasswordField />

                      <Item
                        label="Confirm password"
                        name="confirm_password"
                        rules={[
                          { message: "Please repeat password", required: true },
                        ]}
                      >
                        <Input.Password placeholder="Enter repeat password" />
                      </Item>
                    </Flex>
                  ) : (
                    <Item
                      label="Status"
                      name="status"
                      rules={[
                        { message: "Please select currency", required: true },
                      ]}
                    >
                      <Select options={account.availableUpdateStatusOptions} />
                    </Item>
                  )}
                </Flex>
              </Col>
              <Col span={12}>
                <Flex gap={16} style={{ width: "100%" }} vertical>
                  <Typography.Title style={{ fontSize: 16, opacity: 0.65 }}>
                    ACCESS
                  </Typography.Title>
                  <Flex vertical>
                    {access.selectOptions.map((a) => (
                      <Flex
                        align="center"
                        className={css.accessItem}
                        gap={16}
                        justify="space-between"
                        key={a.value}
                      >
                        <Typography.Text strong>{a.label}</Typography.Text>
                        <Form.Item
                          initialValue={a.value === "view"}
                          name={["access_control_groups", a.value]}
                          noStyle
                        >
                          <Switch disabled={a.value == "view"} />
                        </Form.Item>
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              </Col>
            </Row>
          </Flex>
        </Form>

        {ID && account.data ? (
          <SubMerchChangePasswordForm
            isLoading={isPasswordLoading}
            onChangePassword={changePassword}
          />
        ) : null}
      </>
    </DefaultPageLayout>
  )
})
